// client/src/components/returns/SaleSearch.tsx

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Search,
  Calendar,
  User,
  ShoppingCart,
  Clock,
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { getCurrentShop } from "@/lib/auth";

interface SaleSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectSale: (saleId: string) => void;
}

export function SaleSearch({
  open,
  onOpenChange,
  onSelectSale,
}: SaleSearchProps) {
  const currentShop = getCurrentShop();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"id" | "customer" | "date">(
    "id",
  );
  const [searchDate, setSearchDate] = useState("");

  // Query para buscar ventas
  const {
    data: salesData,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [
      "search-sales",
      currentShop?.id,
      searchTerm,
      searchType,
      searchDate,
    ],
    queryFn: async () => {
      if (!currentShop) return { sales: [] };

      let url = `/api/shops/${currentShop.id}/sales/search?`;

      if (searchType === "id" && searchTerm) {
        url += `saleId=${encodeURIComponent(searchTerm)}`;
      } else if (searchType === "customer" && searchTerm) {
        url += `customer=${encodeURIComponent(searchTerm)}`;
      } else if (searchType === "date" && searchDate) {
        url += `date=${encodeURIComponent(searchDate)}`;
      } else {
        url += `limit=20`;
      }

      const response = await fetch(url);
      const data = await response.json();
      return data;
    },
    enabled: false,
  });

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSearchDate("");
      refetch();
    }
  }, [open, refetch]);

  const sales = salesData?.sales || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Buscar Venta para Devolución</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 sm:py-4">
          {/* Tipo de búsqueda - Botones responsive */}
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <Button
              variant={searchType === "id" ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchType("id")}
              className="flex-1 sm:flex-none"
            >
              Por ID
            </Button>
            <Button
              variant={searchType === "customer" ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchType("customer")}
              className="flex-1 sm:flex-none"
            >
              Por Cliente
            </Button>
            <Button
              variant={searchType === "date" ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchType("date")}
              className="flex-1 sm:flex-none"
            >
              Por Fecha
            </Button>
          </div>

          {/* Campo de búsqueda según tipo */}
          <div className="space-y-2">
            <Label>
              {searchType === "id" && "ID de la Venta"}
              {searchType === "customer" && "Nombre o teléfono del cliente"}
              {searchType === "date" && "Fecha"}
            </Label>

            <div className="flex flex-col sm:flex-row gap-2">
              {searchType === "date" ? (
                <Input
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  className="flex-1"
                />
              ) : (
                <Input
                  placeholder={
                    searchType === "id"
                      ? "Ej: 123e4567-e89b-12d3-a456-426614174000"
                      : "Nombre o teléfono del cliente"
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
              )}
              <Button
                onClick={() => refetch()}
                disabled={isFetching}
                className="w-full sm:w-auto"
              >
                {isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Buscar
              </Button>
            </div>
          </div>

          {/* Resultados */}
          {isFetching ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2">Buscando ventas...</span>
            </div>
          ) : sales && sales.length > 0 ? (
            <>
              {/* Versión Desktop - Tabla */}
              <div className="hidden md:block border rounded-lg overflow-hidden mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Método</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead className="text-right">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.map((sale: any) => (
                      <TableRow key={sale.id}>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            {format(
                              new Date(sale.createdAt),
                              "dd/MM/yyyy HH:mm",
                              { locale: es },
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {sale.id.substring(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            {sale.customerName || "Cliente ocasional"}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(parseFloat(sale.total), "CUP")}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {sale.paymentMethod === "efectivo"
                              ? "Efectivo"
                              : sale.paymentMethod === "transferencia"
                                ? "Transferencia"
                                : "Tarjeta"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">
                            {sale.itemCount || "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => {
                              onSelectSale(sale.id);
                              onOpenChange(false);
                            }}
                          >
                            Seleccionar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Versión Móvil - Tarjetas */}
              <div className="md:hidden space-y-3 mt-4">
                {sales.map((sale: any) => (
                  <Card key={sale.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {format(
                              new Date(sale.createdAt),
                              "dd/MM/yyyy HH:mm",
                              { locale: es },
                            )}
                          </div>
                          <p className="font-mono text-xs mt-1">
                            ID: {sale.id.substring(0, 8)}...
                          </p>
                        </div>
                        <Badge variant="outline">
                          {sale.paymentMethod === "efectivo"
                            ? "Efectivo"
                            : sale.paymentMethod === "transferencia"
                              ? "Transferencia"
                              : "Tarjeta"}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-1 text-sm mb-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="truncate">
                          {sale.customerName || "Cliente ocasional"}
                        </span>
                      </div>

                      <div className="flex justify-between items-center mt-3 pt-3 border-t">
                        <div>
                          <span className="text-xs text-muted-foreground">
                            Total
                          </span>
                          <p className="text-lg font-bold text-primary">
                            {formatCurrency(parseFloat(sale.total), "CUP")}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-muted-foreground">
                            Items
                          </span>
                          <p>
                            <Badge variant="secondary">
                              {sale.itemCount || "N/A"}
                            </Badge>
                          </p>
                        </div>
                      </div>

                      <Button
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => {
                          onSelectSale(sale.id);
                          onOpenChange(false);
                        }}
                      >
                        Seleccionar esta venta
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            salesData && (
              <Card className="mt-4">
                <CardContent className="pt-6 text-center text-muted-foreground">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-20" />
                  <p>No se encontraron ventas</p>
                  <p className="text-sm mt-1">
                    Intenta con otros criterios de búsqueda
                  </p>
                </CardContent>
              </Card>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
